/**
 * Visitor Tracker — captura visitas anônimas + UTMs no Supabase.
 *
 * LGPD (mesmo padrão de `src/lib/utmCapture.ts`):
 *  - `visitor_id` é UUID aleatório, sem PII, de 1ª parte.
 *  - sessionStorage: gravado SEMPRE (escopo da visita atual — é o que permite
 *    ligar a visita anônima ao cadastro feito na mesma sessão).
 *  - localStorage: só APÓS consent aceito (retenção cross-session).
 *  - Recusa explícita: nada persiste além da sessão.
 *
 * Nenhuma ferramenta de terceiros roda antes do consent — isto é medição de
 * 1ª parte no nosso próprio banco (`public_visits`).
 */
/** Import dinâmico: 172 KB de cliente não podem entrar no primeiro
 *  carregamento de página pública só para registrar a visita. */
const cliente = async () => (await import("@/integrations/supabase/client")).supabase;
import { isCookieConsentAccepted } from "@/lib/cookieConsent";

const VISITOR_KEY = "m360_visitor_id";
const UTM_KEY = "m360_utm";
const LAST_VISIT_KEY = "m360:last_public_visit";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const DUPLICATE_VISIT_WINDOW_MS = 3_000;

/** Lê de localStorage (durável) com fallback em sessionStorage (visita atual). */
function readEither(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Grava sempre na sessão; no localStorage só com consent aceito. */
function writeScoped(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignora storage restrito */
  }
  if (isCookieConsentAccepted()) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignora storage restrito */
    }
  }
}

function getOrCreateVisitorId(): string {
  const existing = readEither(VISITOR_KEY);
  if (existing) {
    // Se o consent chegou depois, garante a promoção para localStorage.
    writeScoped(VISITOR_KEY, existing);
    return existing;
  }
  const id = crypto.randomUUID();
  writeScoped(VISITOR_KEY, id);
  return id;
}

/**
 * Garante um visitor_id (cria se não existir) e devolve.
 * Usado pela telemetria pré-conta (`src/lib/preauthEvents.ts`), que precisa de
 * chave antes de qualquer visita ter sido registrada — no app nativo a primeira
 * abertura não passa pela landing.
 */
export function ensureVisitorId(): string | null {
  try {
    return getOrCreateVisitorId();
  } catch {
    return null;
  }
}

/**

 * Promove o visitor_id (e os UTMs) da sessão para o localStorage.
 * Chamar quando o consent virar "accepted" — preserva o mesmo ID.
 */
export function promoteVisitorIdToLocalStorage(): void {
  if (!isCookieConsentAccepted()) return;
  try {
    for (const key of [VISITOR_KEY, UTM_KEY]) {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue && !localStorage.getItem(key)) {
        localStorage.setItem(key, sessionValue);
      }
    }
  } catch {
    /* ignora storage restrito */
  }
}

function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPad/i.test(ua)) return "mobile";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  return "desktop";
}

function isServiceWorkerCleanupNavigation(): boolean {
  try {
    return new URLSearchParams(window.location.search).has("sw-cleanup");
  } catch {
    return false;
  }
}

function cleanupServiceWorkerParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("sw-cleanup")) return;
    url.searchParams.delete("sw-cleanup");
    const clean = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, document.title, clean);
  } catch {
    /* ignora histórico indisponível */
  }
}

function normalizedReferer(): string | null {
  try {
    if (!document.referrer) return null;
    const ref = new URL(document.referrer);
    if (ref.origin === window.location.origin && ref.pathname === "/sw.js") return null;
    return document.referrer;
  } catch {
    return document.referrer || null;
  }
}

function shouldSkipDuplicateVisit(path: string): boolean {
  try {
    const raw = sessionStorage.getItem(LAST_VISIT_KEY);
    const now = Date.now();
    if (raw) {
      const last = JSON.parse(raw) as { path?: string; ts?: number };
      if (last.path === path && typeof last.ts === "number" && now - last.ts < DUPLICATE_VISIT_WINDOW_MS) {
        return true;
      }
    }
    sessionStorage.setItem(LAST_VISIT_KEY, JSON.stringify({ path, ts: now }));
  } catch {
    /* sem sessionStorage, segue rastreando normalmente */
  }
  return false;
}

/** Captura UTMs da URL e persiste por 30 dias (last-touch attribution). */
export function captureUTMs() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms: Record<string, string> = {};
    let found = false;
    for (const f of UTM_FIELDS) {
      const v = params.get(f);
      if (v) {
        utms[f] = v;
        found = true;
      }
    }
    if (found) {
      writeScoped(UTM_KEY, JSON.stringify({ ...utms, ts: Date.now() }));
    }
  } catch {
    /* ignora URL/storage indisponível */
  }
}

function getStoredUTMs(): Record<string, string | null> {
  try {
    const raw = readEither(UTM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Expira em 30 dias
    if (Date.now() - parsed.ts > 30 * 24 * 60 * 60 * 1000) {
      try {
        localStorage.removeItem(UTM_KEY);
        sessionStorage.removeItem(UTM_KEY);
      } catch {
        /* ignora */
      }
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/**
 * Registra uma visita de página no Supabase.
 *
 * [14/09/2026] Deixou de ser silencioso. O INSERT não lança em erro de banco —
 * devolve `{ error }` —, então o catch em volta nunca via recusa de RLS, grant
 * ou timeout: a visita simplesmente não existia. `public_visits` é o
 * denominador de todo o painel de tráfego, e decisão de investimento saía
 * desse número. Falha agora deixa rastro em `preauth_events`.
 */
export async function trackVisit(path: string) {
  try {
    if (isServiceWorkerCleanupNavigation()) {
      cleanupServiceWorkerParam();
      return;
    }
    if (shouldSkipDuplicateVisit(path)) return;

    const utms = getStoredUTMs();
    const { error } = await (await cliente()).from("public_visits").insert({
      visitor_id: getOrCreateVisitorId(),
      path,
      referer: normalizedReferer(),
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      utm_content: utms.utm_content || null,
      utm_term: utms.utm_term || null,
      device: detectDevice(),
      user_agent: navigator.userAgent.slice(0, 500),
    });

    if (error) {
      console.error("[trackVisit] public_visits recusada:", error.code, error.message);
      reportVisitFailure(`${error.code ?? "?"}: ${error.message ?? ""}`);
    }
  } catch (e) {
    // Nunca quebra a UX — mas nunca mais fica sem rastro.
    console.error("[trackVisit] falhou:", e);
    reportVisitFailure(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Import tardio para evitar ciclo: `preauthEvents` importa `ensureVisitorId`
 * deste módulo. Só roda em falha, então o custo é zero no caminho normal.
 */
function reportVisitFailure(reason: string) {
  void import("@/lib/preauthEvents")
    .then(({ trackPreauth }) => {
      trackPreauth("visit_record_failed", { reason: reason.slice(0, 200) });
    })
    .catch(() => {
      /* nada mais a fazer: já está no console */
    });
}

/**
 * Retorna o visitor_id já persistido (sem criar um novo).
 * Usado no signup/login para ligar a visita anônima à conta.
 */
export function getVisitorIdIfPresent(): string | null {
  return readEither(VISITOR_KEY);
}
